// Copyright (c) 2024 - Present. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { handleAPIError } from '@/api/error-utils';
import { useSupportError } from '@/hooks/use-support-error';
import {
  type LocationDataUploadResult,
  uploadLocationData,
  uploadLocationDataResponseSchema,
} from '@/location/api/upload-location-data';
import { CsvUpload } from '@/location/components/location-data-upload/csv-upload';
import { Results } from '@/location/components/location-data-upload/results';
import { Stepper } from '@/location/components/location-data-upload/stepper';
import { TemplateDownload } from '@/location/components/location-data-upload/template-download';
import type {
  ExpandedLocationsState,
  Step,
} from '@/location/components/location-data-upload/types';
import { Box, CardContent } from '@mui/material';
import { captureException, withScope } from '@sentry/react';
import { useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { ChangeEvent, useCallback, useRef, useState } from 'react';

interface LocationDataUploadProps {
  onClose: () => void;
}

export function LocationDataUpload(props: LocationDataUploadProps) {
  const queryClient = useQueryClient();
  const { showSupportError } = useSupportError();
  const [currentStep, setCurrentStep] = useState<Step>('download-template');
  const [file, setFile] = useState<File | null>(null);
  const [uploadResult, setUploadResult] =
    useState<LocationDataUploadResult | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadErrorMessage, setUploadErrorMessage] = useState<string | null>(
    null
  );
  const [uploadLineCount, setUploadLineCount] = useState<number | undefined>(
    undefined
  );
  const [expandedLocations, setExpandedLocations] =
    useState<ExpandedLocationsState>({
      created: false,
      updated: false,
      notUpdated: false,
      failed: false,
      structureErrors: false,
    });
  const [hasFatalError, setHasFatalError] = useState(false);

  const clearFile = () => {
    setFile(null);
    setUploadLineCount(undefined);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const onClose = () => {
    props.onClose();
  };

  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleSelectFile = useCallback(() => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  }, []);

  const handleFileChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const selectedFile = event.target.files?.[0];
      if (selectedFile) {
        setFile(selectedFile);
        setUploadErrorMessage(null);

        const reader = new FileReader();
        reader.onload = (e) => {
          const text = e.target?.result as string;
          const lines = text.split('\n').filter((line) => line.trim() !== '');
          setUploadLineCount(Math.max(0, lines.length - 1));
        };
        reader.readAsText(selectedFile);
      }
    },
    []
  );

  const handleUpload = useCallback(async () => {
    if (!file) return;

    setIsUploading(true);
    setUploadErrorMessage(null);

    try {
      const formData = new FormData();
      formData.append('data', file);

      const result = await uploadLocationData(formData);
      clearFile();
      setUploadResult(result);
      setCurrentStep('results');
      if (result.success) {
        queryClient.invalidateQueries({ queryKey: ['location'] });
      }
    } catch (error) {
      clearFile();
      const axiosError = error as AxiosError;

      // First try to parse as a response with validation errors
      const knownError = uploadLocationDataResponseSchema.safeParse(
        axiosError.response?.data
      );
      if (knownError.success) {
        const result = knownError.data.data;
        // Only navigate to results if there's actual content to display
        const hasContent =
          result.items.length > 0 ||
          result.structureErrors.length > 0 ||
          result.rowErrors.length > 0;
        if (hasContent) {
          setUploadResult(result);
          setUploadErrorMessage(null);
          setCurrentStep('results');
          return;
        }
        // Fall through to handleAPIError to show the error message
      }

      // Could not parse as validation errors - use generic error handler
      const apiError = handleAPIError(axiosError);

      // 4xx errors are client errors - show inline in dialog
      if (apiError.httpStatusCode >= 400 && apiError.httpStatusCode < 500) {
        setUploadErrorMessage(apiError.message);
        return;
      }

      // 5xx or network errors - show global error snackbar
      if (!apiError.sentToSentry) {
        withScope((scope) => {
          scope.setContext('upload', {
            fileSize: file?.size,
            lineCount: uploadLineCount,
          });
          captureException(error);
        });
      }
      setHasFatalError(true);
      showSupportError('An error occurred while uploading location data.');
    } finally {
      setIsUploading(false);
    }
  }, [file, queryClient, showSupportError, uploadLineCount]);

  const handleToggleExpanded = useCallback(
    (section: keyof ExpandedLocationsState) => {
      setExpandedLocations((prev) => ({
        ...prev,
        [section]: !prev[section],
      }));
    },
    []
  );

  const handleStartNew = useCallback(() => {
    setCurrentStep('download-template');
    setFile(null);
    setUploadResult(null);
    setUploadErrorMessage(null);
    setUploadLineCount(undefined);
    setExpandedLocations({
      created: false,
      updated: false,
      notUpdated: false,
      failed: false,
      structureErrors: false,
    });
  }, []);

  const handleBack = useCallback(() => {
    if (currentStep === 'upload-csv') {
      setCurrentStep('download-template');
      setHasFatalError(false);
    } else if (currentStep === 'results') {
      setCurrentStep('upload-csv');
    }
  }, [currentStep]);

  const handleNext = useCallback(() => {
    if (currentStep === 'download-template') {
      setCurrentStep('upload-csv');
    }
  }, [currentStep]);

  return (
    <Box>
      <CardContent>
        <Stepper currentStep={currentStep} />

        {currentStep === 'download-template' && (
          <TemplateDownload onBack={onClose} onNext={handleNext} />
        )}

        {currentStep === 'upload-csv' && (
          <CsvUpload
            uploadErrorMessage={uploadErrorMessage}
            file={file}
            isUploading={isUploading}
            onSelectFile={handleSelectFile}
            onUpload={handleUpload}
            onBack={handleBack}
            onClose={onClose}
            hasFatalError={hasFatalError}
            uploadLineCount={uploadLineCount}
          />
        )}

        {currentStep === 'results' && (
          <Results
            uploadResult={uploadResult}
            expandedLocations={expandedLocations}
            onToggleExpanded={handleToggleExpanded}
            onBack={handleBack}
            onStartNew={handleStartNew}
            onClose={onClose}
          />
        )}

        <input
          ref={fileInputRef}
          type="file"
          accept=".csv"
          style={{ display: 'none' }}
          onChange={handleFileChange}
        />
      </CardContent>
    </Box>
  );
}
